import { VersionPill } from '@/components/admin/versioned/version-pill';
import { referentTitle } from '@/lib/versioned-labels';
import type { UsageLine, VersionTag } from '@matter_extractor/shared';
import { VERSION_TAGS } from '@matter_extractor/shared';

/**
 * Who would play a version, listed one per row (refonte modulaire §4).
 *
 * The « effet » the three boxes of this screen exist to show. Filtered BY TAG,
 * because that is the question each box asks: a save changes what the
 * `développement` referents play, a passage to production changes what the
 * `production` ones play, and mixing the two lists would make an operator
 * ticking « Passer aussi en production » unable to see what the tick added.
 *
 * Only TAGGED versions count, and that is the pattern's rule rather than a
 * filter applied here: `versionedUsageSchema` carries the tags a referent's
 * pointers sit on, and « une citation par une version historique sans tag n'est
 * pas un usage - aucun lien ne peut plus la jouer » (§4).
 */

export interface ReferentRowsProps {
  /** The `data-testid` prefix of the object: « critere » (`VersionedWording.entity`). */
  entity: string;
  usage: readonly UsageLine[];
  /**
   * Keep only the referents whose pointer of this tag cites the entity - or
   * `all` for the retirement, which hits both pointers at once and would
   * suggest a choice that does not exist if it split the list in two.
   */
  tag: VersionTag | 'all';
  /** What to say when nothing does - « utilisé nulle part encore » (§4). */
  emptyMessage: string;
  /**
   * The lines ARE the referents of `tag` already, each carrying its OWN
   * pointers that play it (review of !462, issue #148): drawn as given, with
   * those pills. The texts of the noyaux need it - an experience reads them
   * under the tag of its reference, which the rule below cannot see (§6.5).
   * Absent, the layer's rule: keep the referents whose pointer of `tag` cites
   * the entity, and pill `tag`.
   */
  byReference?: boolean;
}

export function ReferentRows({
  entity,
  usage,
  tag,
  emptyMessage,
  byReference = false,
}: ReferentRowsProps) {
  const asGiven = byReference || tag === 'all';
  const rows = asGiven ? usage : usage.filter((referent) => referent.tags.includes(tag));
  if (rows.length === 0) {
    return (
      <p
        data-testid={`${entity}-referents-${tag}-vide`}
        className="text-gray-500 text-small italic"
      >
        {emptyMessage}
      </p>
    );
  }
  return (
    <div data-testid={`${entity}-referents-${tag}`} className="space-y-2">
      {rows.map((referent) => (
        <div
          key={`${referent.kind}-${referent.id}`}
          className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2.5"
        >
          <p className="min-w-0 break-words text-gray-800 text-small">
            {referentTitle(referent)}
            {referent.via === null ? null : (
              <span className="text-gray-600"> par {referent.via}</span>
            )}
          </p>
          <span className="flex shrink-0 flex-wrap gap-1.5">
            {/* One pill for the tag asked about, or every tag the referent
                carries - walked in the catalogue's order so two rows saying the
                same thing say it the same way. */}
            {(asGiven ? VERSION_TAGS.filter((one) => referent.tags.includes(one)) : [tag]).map(
              (one) => (
                <VersionPill key={one} entity={entity} tag={one} />
              ),
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
